import React from 'react';
import { colors, fonts, sectionLabelStyle, gradients } from '../../patientintel/styles';
import { Highlight } from '../../patientintel/Highlight';

// Visual treatment combines patientintel/WhatIsPulseCheckSection
// (header + callout) with patientintel/HowItWorksSection (3-step grid).
// Copy is sourced from GSK_COPY_FOR_REVIEW.md [INTELLIGENCE].

const stepShape = 'polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)';

const steps = [
  {
    n: 1,
    badge: 'AI-Assisted Intake',
    title: 'Your team identifies a need',
    body: 'Our AI Research Assistant refines your question into a clear, answerable patient inquiry — often in a single conversation that replaces weeks of scoping.',
  },
  {
    n: 2,
    badge: 'Community Activation',
    title: 'We reach the right patients',
    body: 'Targeted by condition, treatment stage, care pathway, and geography — drawing from existing intelligence or fielding fresh input.',
  },
  {
    n: 3,
    badge: 'Results in Days',
    title: 'You receive patient intelligence',
    body: "Themes, patterns, and contextual patient voice — building on everything we've learned before, compounding over time.",
  },
];

export const IntelligenceSection = () => (
  <section
    style={{
      backgroundColor: colors.lightGray,
      padding: '96px 48px',
      boxSizing: 'border-box',
    }}
  >
    <style>{`
      @media (max-width: 768px) {
        .gsk-intel-wrap { padding-left: 24px !important; padding-right: 24px !important; padding-top: 64px !important; padding-bottom: 64px !important; }
        .gsk-intel-callout { padding: 28px !important; }
        .gsk-intel-steps { grid-template-columns: 1fr !important; gap: 32px !important; }
      }
    `}</style>
    <div className="gsk-intel-wrap" style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ maxWidth: '780px', margin: '0 auto', textAlign: 'center' }}>
        <p style={{ ...sectionLabelStyle, textAlign: 'center' }}>The Intelligence Layer</p>
        <h2
          style={{
            fontFamily: fonts.serif,
            fontSize: 'clamp(26px, 3vw, 40px)',
            fontWeight: 700,
            lineHeight: 1.2,
            color: colors.charcoal,
            margin: '0 0 24px',
          }}
        >
          On-demand patient intelligence
          <br />
          <span style={{ fontStyle: 'italic', fontWeight: 400 }}>
            that <Highlight>compounds</Highlight> over time.
          </span>
        </h2>
        <p
          style={{
            fontFamily: fonts.sans,
            fontSize: '17px',
            lineHeight: 1.75,
            color: colors.warmGray,
            maxWidth: '680px',
            margin: '0 auto',
            fontWeight: 400,
            textWrap: 'pretty',
          }}
        >
          After years of helping clients conduct patient research, we saw an opportunity to scale how we engage with the patient voice. With decreasing timelines and novel approaches, we created a living, queryable intelligence base that makes uncovering real patient insights faster, deeper, and more robust than the last.
        </p>
      </div>

      <div
        className="gsk-intel-callout"
        style={{
          background: `${gradients.primarySoft}, ${colors.white}`,
          borderRadius: '14px',
          padding: '32px 36px',
          maxWidth: '720px',
          margin: '56px auto 0',
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
          border: '1px solid rgba(235,160,155,0.22)',
        }}
      >
        <div
          style={{
            fontFamily: fonts.sans,
            fontSize: 'clamp(20px, 3.5vw, 30px)',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: colors.deepCoral,
            marginBottom: '22px',
            lineHeight: 1.2,
          }}
        >
          Pulse Check<sup style={{ fontSize: '0.5em', verticalAlign: 'super' }}>™</sup>
        </div>
        <div
          style={{
            fontFamily: fonts.serif,
            fontSize: '22px',
            fontWeight: 700,
            color: colors.charcoal,
            marginBottom: '14px',
            lineHeight: 1.35,
          }}
        >
          A focused, rapid patient inquiry — returned in days.
        </div>
        <div
          style={{
            fontFamily: fonts.sans,
            fontSize: '15px',
            color: colors.warmGray,
            lineHeight: 1.7,
            fontWeight: 400,
            textWrap: 'pretty',
          }}
        >
          Your team brings the question. We shape it, deploy it, and return structured intelligence from real patients. Each Pulse Check adds to a longitudinal intelligence base that deepens with every engagement.
        </div>
      </div>

      <div
        className="gsk-intel-steps"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 0,
          marginTop: '80px',
          maxWidth: '1000px',
          marginLeft: 'auto',
          marginRight: 'auto',
          position: 'relative',
        }}
      >
        {steps.map((s) => (
          <div key={s.n} style={{ padding: '0 24px', textAlign: 'center' }}>
            <div
              style={{
                width: '72px',
                height: '60px',
                clipPath: stepShape,
                background: `linear-gradient(135deg, ${colors.coral} 0%, ${colors.peach} 100%)`,
                color: colors.white,
                fontFamily: fonts.serif,
                fontSize: '24px',
                fontWeight: 900,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              {s.n}
            </div>
            <div
              style={{
                display: 'inline-block',
                backgroundColor: colors.coralLight,
                color: colors.coral,
                fontFamily: fonts.sans,
                fontSize: '11px',
                fontWeight: 500,
                letterSpacing: '0.3px',
                padding: '4px 10px',
                borderRadius: '20px',
                marginBottom: '12px',
              }}
            >
              {s.badge}
            </div>
            <div
              style={{
                fontFamily: fonts.sans,
                fontSize: '17px',
                fontWeight: 500,
                color: colors.dark,
                marginBottom: '10px',
                lineHeight: 1.3,
              }}
            >
              {s.title}
            </div>
            <div
              style={{
                fontFamily: fonts.sans,
                fontSize: '15px',
                color: colors.warmGray,
                lineHeight: 1.65,
                fontWeight: 300,
                textWrap: 'pretty',
              }}
            >
              {s.body}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);
